import React, { useEffect, useRef, useState, useCallback } from "react";
import { ScrollText, IdCard, Camera, Home as HomeIcon, Loader2, GraduationCap, Landmark, AlertTriangle, CheckCircle2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchKycStatus, uploadKycDocument, removeKycDocument, submitBci, digilockerStart } from "@lawyer/services/kycApi";
import { fetchVerificationProgress } from "@lawyer/services/lawyerApi";
import LevelCard from "@lawyer/components/LevelCard";
import { KycDocumentRow, STATUS_PILL } from "@lawyer/components/KycDocumentRow";
import { CompactDocRow, DigiLockerSummary, ProfessionalDetails } from "@lawyer/components/kyc/VerifiedSummary";
import { FinancialDocRow } from "@lawyer/components/FinancialDocRow";
import { ChangeRequestModal } from "@lawyer/components/PayoutKycCard";
import { listMyFinancialChanges, cancelFinancialChange } from "@lawyer/services/financialApi";
import { useDigiLockerReturn, digilockerErrorMessage } from "@lawyer/hooks/useDigiLockerReturn";
import VerificationProgressCard from "@lawyer/components/VerificationProgressCard";

const REQUIRED = [
  { key: "aadhaar", label: "Aadhaar (front+back)", icon: IdCard, hint: "Upload a PDF/image, or fetch the UIDAI-issued copy from DigiLocker" },
  { key: "bci_cert", label: "BCI enrolment certificate", icon: ScrollText, hint: "Scan of your Bar Council enrolment certificate" },
  { key: "degree", label: "Law degree certificate", icon: GraduationCap, hint: "LL.B./J.D./equivalent — issuing university degree certificate" },
  { key: "photo", label: "Recent photograph", icon: Camera, hint: "Headshot, JPG/PNG, used on your profile" },
  { key: "address_proof", label: "Address proof", icon: HomeIcon, hint: "Utility bill / passport / driving license — last 3 months" },
];

const DOC_LABEL = Object.fromEntries(REQUIRED.map((r) => [r.key, r.label]));
const SHORT_LABEL = { ...DOC_LABEL, aadhaar: "Aadhaar", pan: "PAN" };

export default function LawyerKyc() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [requests, setRequests] = useState([]);
  const [requestFor, setRequestFor] = useState(null);

  const [progress, setProgress] = useState(undefined); // undefined = in flight, null = failed (card self-loads)
  const progressFetched = useRef(false);

  const reload = useCallback(async () => {
    // KYC status, financial change requests and the verification matrix are independent — fetch concurrently.
    // The matrix is prefetched once; later reloads let VerificationProgressCard refresh itself via refreshKey.
    const first = !progressFetched.current;
    progressFetched.current = true;
    const [status, changes, matrix] = await Promise.allSettled([
      fetchKycStatus(), listMyFinancialChanges({ limit: 20 }), first ? fetchVerificationProgress() : Promise.resolve(undefined),
    ]);
    if (status.status === "fulfilled") setData(status.value);
    else toast.error(status.reason?.response?.data?.detail || "Could not load KYC status");
    if (changes.status === "fulfilled" && changes.value) setRequests(changes.value.items || []);
    /* financial requests are optional on this page */
    if (first) setProgress(matrix.status === "fulfilled" ? matrix.value : null);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const [dlReturn, dismissDlReturn] = useDigiLockerReturn(reload);

  const onFile = async (docType, file) => {
    if (!file) return;
    setBusy(`upload-${docType}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      await uploadKycDocument(fd);
      toast.success("Uploaded — waiting for review");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };

  const onDigiLocker = async (docType) => {
    const docTypes = Array.isArray(docType) ? docType : [docType];
    setBusy(`digilocker-${docTypes.join("+")}`);
    try {
      const r = await digilockerStart(docTypes);
      window.location.assign(r.authorization_url);
    } catch (e) {
      toast.error(digilockerErrorMessage(e, "Could not start DigiLocker"));
      setBusy(null);
    }
  };

  const onRemove = async (key, doc) => {
    setBusy(`remove-${key}`);
    try {
      await removeKycDocument(doc.id);
      toast.success("Document removed");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not remove document");
      setBusy(null);
    }
  };

  const onCancelRequest = async (req) => {
    try { await cancelFinancialChange(req.id); toast.info("Request cancelled"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="text-slate-500" data-testid="lawyer-kyc-loading">Loading…</div>;

  const digilockerTypes = new Set(data.digilocker_doc_types || []);
  const docByType = {};
  (data.documents || []).forEach((d) => {
    if (!docByType[d.doc_type] || new Date(d.uploaded_at) > new Date(docByType[d.doc_type].uploaded_at)) {
      docByType[d.doc_type] = d;
    }
  });
  const financial = data.financial || {};
  const dlDocByType = {};
  (data.documents || []).forEach((d) => {
    if (d.source !== "digilocker") return;
    if (!dlDocByType[d.doc_type] || new Date(d.uploaded_at) > new Date(dlDocByType[d.doc_type].uploaded_at)) dlDocByType[d.doc_type] = d;
  });
  const pendingByField = {};
  requests.forEach((r) => { if (["pending_otp", "pending_approval"].includes(r.status)) pendingByField[r.field] = r; });
  const needsDl = (t) => digilockerTypes.has(t) && !(docByType[t] && docByType[t].status !== "rejected")
    && !(t === "pan" && (financial.pan?.status === "verified_by_admin" || pendingByField.pan));
  const jointFetch = needsDl("aadhaar") && needsDl("pan");
  const financialRow = (field) => (
    <FinancialDocRow
      key={field}
      field={field}
      current={financial[field]}
      pendingRequest={pendingByField[field]}
      digilockerDoc={field === "pan" ? dlDocByType.pan : null}
      busy={busy}
      digilockerSupported={field === "pan" && digilockerTypes.has("pan")}
      onRequest={setRequestFor}
      onCancel={onCancelRequest}
      onDigiLocker={onDigiLocker}
    />
  );

  const complete = progress?.all_met === true;
  const reqDetail = (key) => (progress?.requirements || []).find((r) => r.key === key)?.detail;
  const requirementTargets = complete
    ? {
        aadhaar: "kyc-target-aadhaar",
        pan: "kyc-target-pan",
        bci_cert: "bci-enrolment-certificate",
        degree: "kyc-target-degree",
        photo: "kyc-target-photo",
        address_proof: "kyc-target-address_proof",
        payout_instrument: "kyc-target-payout_instrument",
        lawyer_level: "kyc-target-lawyer_level",
      }
    : {
        aadhaar: "kyc-digilocker-info",
        pan: "kyc-target-pan",
        bci_cert: "bci-enrolment-certificate",
        degree: "kyc-target-degree",
        photo: "kyc-target-photo",
        address_proof: "kyc-target-address_proof",
        payout_instrument: "kyc-target-payout_instrument",
      };
  const scrollToRequirement = (targetId) => {
    document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <div className="mb-6" data-testid="lawyer-kyc-header">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          Verification & e-KYC
        </h1>
        <p className="mt-1 text-sm text-slate-500" data-testid="lawyer-kyc-subtitle">
          {complete
            ? "Your profile is verified. Your approved documents and verification details are shown below."
            : "Verified profiles get 4× more bookings. Submit your documents, PAN and bank account — upload them, or fetch Aadhaar and PAN directly from DigiLocker."}
        </p>
      </div>

      {/* Canonical 10-requirement matrix — same as Admin; also shown on the dashboard until fully verified + listed. */}
      <div className="mb-6" data-testid="kyc-verification-progress">
        {progress !== undefined && (
          <VerificationProgressCard
            refreshKey={JSON.stringify((data?.documents || []).map((d) => d.status))}
            initialData={progress}
            requirementTargets={requirementTargets}
            onRequirementNavigate={scrollToRequirement}
          />
        )}
      </div>

      {dlReturn && (
        <div
          className={`mb-6 rounded-2xl border p-4 flex items-start gap-3 ${dlReturn.status === "error" ? "bg-rose-50 border-rose-200" : "bg-[#FBF4DF] border-[#D4AF37]/40"}`}
          data-testid="kyc-digilocker-return"
        >
          {dlReturn.status === "pending" && <Loader2 size={18} className="animate-spin text-[#0A2540] mt-0.5" />}
          {dlReturn.status === "completed" && <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" />}
          {dlReturn.status === "error" && <AlertTriangle size={18} className="text-rose-600 mt-0.5" />}
          <div className="flex-1 text-sm">
            {dlReturn.status === "pending" && <p className="font-bold text-[#0A2540]">Fetching your document from DigiLocker…</p>}
            {dlReturn.status === "completed" && (
              <>
                <p className="font-bold text-[#0A2540]">{DOC_LABEL[dlReturn.docType] || "Document"} fetched from DigiLocker</p>
                <p className="text-slate-600">It's now under review — no manual upload needed for this document.</p>
              </>
            )}
            {dlReturn.status === "error" && (
              <>
                <p className="font-bold text-rose-700">DigiLocker fetch did not complete</p>
                <p className="text-rose-700/80" data-testid="kyc-digilocker-return-error">{dlReturn.message} You can try again or upload the document instead.</p>
              </>
            )}
          </div>
          {dlReturn.status !== "pending" && (
            <button className="text-xs font-bold text-slate-500 underline" onClick={dismissDlReturn} data-testid="kyc-digilocker-return-dismiss">Dismiss</button>
          )}
        </div>
      )}

      {complete ? (
        <div className="space-y-4 mb-8" data-testid="kyc-verified-summary">
          <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 -mb-1">Verified documents</h2>
          <div className="space-y-2">
            {REQUIRED.map((def) => (
              <div key={def.key}
                id={def.key === "bci_cert" ? "bci-enrolment-certificate" : `kyc-target-${def.key}`}
                className="scroll-mt-24">
                <CompactDocRow testKey={def.key} label={SHORT_LABEL[def.key] || def.label} icon={def.icon} doc={docByType[def.key]} hint={def.hint}
                               status={STATUS_PILL[docByType[def.key]?.status]?.label || "Approved"} />
                {def.key === "aadhaar" && (
                  <div id="kyc-target-pan" className="scroll-mt-24">
                    <CompactDocRow testKey="pan" label="PAN" icon={CreditCard} doc={dlDocByType.pan} masked={financial.pan?.masked} status="Verified" />
                  </div>
                )}
              </div>
            ))}
            <div id="kyc-target-payout_instrument" className="scroll-mt-24">
              <CompactDocRow testKey="bank" label="Bank account" icon={Landmark} masked={financial.bank_account?.masked} status="Verified" />
            </div>
          </div>
          <div id="kyc-target-lawyer_level" className="scroll-mt-24">
            <ProfessionalDetails kycBci={data.kyc_bci} bank={financial.bank_account} levelDetail={reqDetail("lawyer_level")} />
          </div>
          <DigiLockerSummary dlDocs={dlDocByType} labels={SHORT_LABEL} />
        </div>
      ) : (
        <>
      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="scroll-mt-24">
          <BciCard kycBci={data.kyc_bci} onDone={reload} />
        </div>
        <div id="kyc-digilocker-info" className="scroll-mt-24 bg-white border border-slate-200 rounded-2xl p-5 flex gap-3" data-testid="kyc-digilocker-info">
          <div className="h-10 w-10 rounded-xl bg-[#0A2540] text-[#D4AF37] grid place-items-center shrink-0"><Landmark size={18} /></div>
          <div className="text-sm">
            <p className="font-bold text-[#0A2540]">DigiLocker</p>
            <p className="text-xs text-slate-500 mt-1">
              {digilockerTypes.size > 0
                ? "Fetch your UIDAI-issued Aadhaar and Income Tax–issued PAN straight from DigiLocker — one sign-in with your Aadhaar-linked mobile, nothing to scan or upload. Other documents are upload-only."
                : "DigiLocker fetch is temporarily unavailable — please upload your documents instead."}
            </p>
            {jointFetch && (
              <Button size="sm" onClick={() => onDigiLocker(["aadhaar", "pan"])} disabled={!!busy} data-testid="kyc-digilocker-fetch-both"
                className="mt-3 rounded-xl text-xs h-9 font-bold bg-[#0A2540] text-[#D4AF37] hover:opacity-90">
                {busy === "digilocker-aadhaar+pan" ? <Loader2 size={14} className="animate-spin mr-1" /> : <Landmark size={12} className="mr-1" />}
                Fetch Aadhaar &amp; PAN together
              </Button>
            )}
          </div>
        </div>
      </div>

      <h2 className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-3">Required documents</h2>
      <div className="space-y-3 mb-8">
        {REQUIRED.map((def) => (
          <div key={def.key}
            id={def.key === "bci_cert" ? "bci-enrolment-certificate" : (def.key === "aadhaar" ? undefined : `kyc-target-${def.key}`)}
            className={def.key === "aadhaar" ? undefined : "scroll-mt-24"}>
            <KycDocumentRow
              def={def}
              doc={docByType[def.key]}
              busy={busy}
              digilockerSupported={digilockerTypes.has(def.key)}
              onFile={onFile}
              onDigiLocker={onDigiLocker}
              onRemove={onRemove}
            />
            {def.key === "aadhaar" && (
              <div id="kyc-target-pan" className="mt-3 scroll-mt-24">
                {financialRow("pan")}
              </div>
            )}
          </div>
        ))}
        <div id="kyc-target-payout_instrument" className="scroll-mt-24">
          {financialRow("bank_account")}
        </div>
      </div>
        </>
      )}

      <LevelCard
        actionLabel="View Pricing & Request Level Change"
        actionLabelUnassigned="Request Level Assignment"
        hideDetailsLink
        className="mb-8"
        data-testid="kyc-lawyer-level-card"
      />

      <ChangeRequestModal
        open={!!requestFor}
        field={requestFor}
        currentValue={null}
        onClose={() => setRequestFor(null)}
        onSubmitted={reload}
      />
    </>
  );
}

function BciCard({ kycBci, onDone }) {
  const [enrol, setEnrol] = useState(kycBci?.enrolment_number || "");
  const [name, setName] = useState(kycBci?.name || "");
  const [busy, setBusy] = useState(false);

  const submitted = !!kycBci?.enrolment_number;

  const submit = async () => {
    if (!enrol.trim()) { toast.error("Enrolment number required"); return; }
    setBusy(true);
    try {
      await submitBci(enrol.trim(), name.trim());
      toast.success("Submitted for verification");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="kyc-bci-card">
      <div className="flex items-center gap-2">
        <ScrollText size={16} className="text-[#0A2540]" />
        <p className="font-bold text-[#0A2540]">Bar Council enrolment</p>
        {kycBci?.status === "verified" && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="kyc-bci-status">
            Verified
          </span>
        )}
        {kycBci?.status === "manual_review" && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="kyc-bci-status">
            Under review
          </span>
        )}
        {kycBci?.status === "rejected" && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5" data-testid="kyc-bci-status">
            Rejected
          </span>
        )}
        {kycBci?.status === "submitted" && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5" data-testid="kyc-bci-status">
            Awaiting certificate
          </span>
        )}
      </div>
      <div className="mt-3 space-y-2">
        <Input
          value={enrol} onChange={(e) => setEnrol(e.target.value)}
          placeholder="MAH/12345/2010"
          disabled={submitted}
          data-testid="kyc-bci-enrol"
          className="rounded-xl"
        />
        <Input
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Name as on certificate"
          disabled={submitted}
          data-testid="kyc-bci-name"
          className="rounded-xl"
        />
        {!submitted && (
          <Button onClick={submit} disabled={busy} data-testid="kyc-bci-submit-btn" className="w-full adv-btn-primary rounded-xl h-9">
            {busy && <Loader2 size={14} className="animate-spin mr-1" />} Submit for review
          </Button>
        )}
        {submitted && (
          <p className="text-xs text-slate-500" data-testid="kyc-bci-note">
            {kycBci?.status === "verified"
              ? `Verified by Admin against your approved Bar Council certificate.`
              : (kycBci?.status_reason || `Submitted on ${kycBci?.submitted_at?.slice(0, 10)}. Our team will verify with the State Bar Council in 1-2 business days.`)}
          </p>
        )}
      </div>
    </div>
  );
}
